import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  ExternalLink,
  Loader2,
  Headphones,
} from 'lucide-react';
import { chatAPI } from '@/lib/api';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { toast } from 'sonner';

export function ChatWidget() {
  const { t, lang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('cv_chat_session');
    if (stored) {
      setSessionId(stored);
    } else {
      const newId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      localStorage.setItem('cv_chat_session', newId);
      setSessionId(newId);
    }
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: t('chat.welcome'),
        time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
      }]);
    }
  }, [isOpen, messages.length, t, lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = {
      role: 'user',
      text,
      time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await chatAPI.sendMessage({
        message: text,
        session_id: sessionId,
        lang,
      });

      const botMsg = {
        role: 'assistant',
        text: res.data.response,
        time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, botMsg]);

      if (res.data.session_id) {
        setSessionId(res.data.session_id);
        localStorage.setItem('cv_chat_session', res.data.session_id);
      }
    } catch (err) {
      const errorMsg = {
        role: 'assistant',
        text: t('chat.errorSend'),
        time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleEscalate = async () => {
    setEscalating(true);
    try {
      const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
      const res = await chatAPI.escalate({
        session_id: sessionId,
        message: lastUserMsg?.text || '',
        page: window.location.pathname,
        lang,
      });
      
      const escalationMsg = {
        role: 'assistant',
        text: res.data.message,
        time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
        isEscalation: true,
        telegramLink: res.data.telegram_link,
      };
      setMessages((prev) => [...prev, escalationMsg]);
      toast.success(t('chat.escalated'));
    } catch (err) {
      toast.error(t('chat.errorSend'));
    } finally {
      setEscalating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = lang === 'tr'
    ? [
        { label: 'Bonus nasil alinir?', msg: 'Hos geldin bonusu nasil alinir?' },
        { label: 'Cekim kosullari', msg: 'Cekim yapmak icin ne yapmam lazim?' },
        { label: 'KYC nedir?', msg: 'KYC nedir ve nasil yapilir?' },
        { label: 'Referans programi', msg: 'Referans programi nasil calisir?' },
      ]
    : [
        { label: 'How to get bonus?', msg: 'How do I get the welcome bonus?' },
        { label: 'Withdrawal rules', msg: 'What do I need to withdraw?' },
        { label: 'What is KYC?', msg: 'What is KYC and how to do it?' },
        { label: 'Referral program', msg: 'How does the referral program work?' },
      ];

  return (
    <>
      {!isOpen && (
        <button
          data-testid="chat-widget-button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 w-[60px] h-[60px] rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(160,84%,29%)] text-[hsl(var(--primary-foreground))] flex items-center justify-center shadow-[0_4px_24px_rgba(0,206,162,0.4)] hover:shadow-[0_6px_32px_rgba(0,206,162,0.55)] hover:scale-105 transition-all duration-300 z-50 active:scale-95 animate-[chatPulse_2.5s_ease-in-out_infinite]"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {isOpen && (
        <div
          data-testid="chat-widget-window"
          className="fixed bottom-[80px] right-5 w-[380px] max-w-[calc(100vw-40px)] h-[520px] max-h-[calc(100vh-96px)] bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t('chat.title')}</p>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2DD36F]" />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Online</span>
                </div>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              data-testid="chat-widget-close"
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 p-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user'
                    ? 'bg-[hsl(var(--secondary))]'
                    : 'bg-[hsl(var(--primary))]/10'
                }`}>
                  {msg.role === 'user' ? (
                    <User className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  ) : (
                    <Bot className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                  )}
                </div>
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 ${
                  msg.role === 'user'
                    ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-br-sm'
                    : 'bg-[hsl(var(--secondary))] rounded-bl-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                  {msg.telegramLink && (
                    <a
                      href={msg.telegramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 mt-2 text-xs text-[hsl(var(--primary))] hover:underline"
                    >
                      Telegram <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <p className={`text-[10px] mt-1 ${
                    msg.role === 'user' ? 'text-[hsl(var(--primary-foreground))]/60' : 'text-[hsl(var(--muted-foreground))]'
                  }`}>{msg.time}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                </div>
                <div className="bg-[hsl(var(--secondary))] rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {t('chat.typing')}
                  </div>
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="space-y-2">
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {t('chat.quickQuestions')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setMessages((prev) => [...prev, {
                          role: 'user',
                          text: action.msg,
                          time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
                        }]);
                        setLoading(true);
                        chatAPI.sendMessage({ message: action.msg, session_id: sessionId, lang })
                          .then((res) => {
                            setMessages((prev) => [...prev, {
                              role: 'assistant',
                              text: res.data.response,
                              time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
                            }]);
                          })
                          .catch(() => {
                            setMessages((prev) => [...prev, {
                              role: 'assistant',
                              text: t('chat.errorSend'),
                              time: new Date().toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }),
                            }]);
                          })
                          .finally(() => setLoading(false));
                      }}
                      className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/10 transition-colors duration-200"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[hsl(var(--border))] p-3 space-y-2">
            {/* Live support escalation */}
            <div className="flex items-center justify-between gap-2">
              <Button
                data-testid="chat-escalate-button"
                variant="ghost"
                size="sm"
                onClick={handleEscalate}
                disabled={escalating}
                className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] gap-1.5"
              >
                <Headphones className="w-3.5 h-3.5" />
                {escalating ? t('chat.escalating') : t('chat.escalateBtn')}
              </Button>
              <a
                href="https://t.me/safecrypto_support"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline"
                data-testid="chat-telegram-link"
              >
                Telegram <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {/* Input */}
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                data-testid="chat-widget-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('chat.placeholder')}
                disabled={loading}
                className="bg-[hsl(var(--background))] border-[hsl(var(--border))] text-sm h-9 rounded-full px-4"
              />
              <Button
                data-testid="chat-widget-send"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                size="sm"
                className="w-9 h-9 p-0 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
